// Step04 关键词展示的中文翻译数据源。
//
// keyword-showcase.js 的渲染契约要求它本身不发任何网络请求（见
// test/step4-keyword-showcase-contract.test.mjs），所以翻译请求集中在
// 本模块：渲染后按整表关键词批量调用 /api/translate-search-terms，
// 结果缓存在会话内；「中文」列展示与「复制整个表格」共用同一份缓存。

import { getState } from '../../../state.js';
import { translateSearchTerms } from '../../../api/keyword-api.js';

// 服务端单次最多接受 120 个词，超过时分批串行请求。
const TRANSLATION_BATCH_LIMIT = 120;
// 请求失败后的冷却时间，避免状态订阅触发的连续重渲染反复打请求。
const TRANSLATION_FAILURE_COOLDOWN_MS = 30_000;

const translationCache = new Map();
let lastFailureAt = 0;
// 串行队列：并发的 ensure 调用依次执行，后来者可直接命中前者写入的缓存。
let inflightQueue = Promise.resolve();

function translationKey(term) {
  return String(term || '').trim().toLowerCase();
}

// Step02 的 Search Terms 翻译结果（stCandidatesTranslationMap）可以直接复用，
// 避免同一批词在 Step04 再翻一次。
function seedFromStep2TranslationMap() {
  const map = getState('stCandidatesTranslationMap');
  if (!map || typeof map !== 'object') return;
  for (const [key, value] of Object.entries(map)) {
    const zh = String(value || '').trim();
    if (zh && !translationCache.has(key)) translationCache.set(key, zh);
  }
}

// 品牌名、型号等不可译词服务端会原样返回，与原文相同时视为无译文。
function displayableTranslation(term) {
  const key = translationKey(term);
  const zh = translationCache.get(key) || '';
  return zh && translationKey(zh) !== key ? zh : '';
}

// 供复制表格等同步场景读取缓存译文；未翻译或与原文相同返回空串。
export function getCachedKeywordTranslation(term) {
  return displayableTranslation(term);
}

async function fetchMissingTranslations(terms) {
  seedFromStep2TranslationMap();
  const missing = [];
  const seenKeys = new Set();
  for (const term of Array.isArray(terms) ? terms : []) {
    const key = translationKey(term);
    if (!key || translationCache.has(key) || seenKeys.has(key)) continue;
    seenKeys.add(key);
    missing.push(String(term).trim());
  }
  if (!missing.length) return;
  if (Date.now() - lastFailureAt < TRANSLATION_FAILURE_COOLDOWN_MS) return;

  try {
    for (let start = 0; start < missing.length; start += TRANSLATION_BATCH_LIMIT) {
      const batch = missing.slice(start, start + TRANSLATION_BATCH_LIMIT);
      const data = await translateSearchTerms({ items: batch });
      const translatedItems = Array.isArray(data?.translatedItems) ? data.translatedItems : [];
      batch.forEach((term, index) => {
        translationCache.set(translationKey(term), String(translatedItems[index] || '').trim());
      });
    }
    lastFailureAt = 0;
  } catch {
    // 失败不写缓存，冷却结束后的下一次渲染或复制会自动重试。
    lastFailureAt = Date.now();
  }
}

// 确保给定关键词的译文进入缓存（缓存命中时不发请求）；复制整表前调用。
export function ensureKeywordTranslations(terms) {
  inflightQueue = inflightQueue
    .then(() => fetchMissingTranslations(terms))
    .catch(() => {});
  return inflightQueue;
}

function applyTranslationToSpan(span, { final = false } = {}) {
  const term = span.dataset.keywordZhFor || '';
  if (translationCache.has(translationKey(term))) {
    span.textContent = displayableTranslation(term) || '—';
    span.classList.remove('is-loading');
  } else if (final) {
    span.textContent = '—';
    span.classList.remove('is-loading');
  }
}

// 渲染后的中文列补水：先回填缓存，再请求整表缺失词并更新最新 DOM。
export async function hydrateKeywordTranslations(container, terms = []) {
  if (!container || typeof container.querySelectorAll !== 'function') return;
  seedFromStep2TranslationMap();

  const spans = [...container.querySelectorAll('[data-keyword-zh-for]')];
  for (const span of spans) applyTranslationToSpan(span);

  const wanted = Array.isArray(terms) && terms.length
    ? terms
    : spans.map((span) => span.dataset.keywordZhFor);
  if (!wanted.length) return;
  await ensureKeywordTranslations(wanted);

  // 请求期间可能已重渲染，按最新 DOM 回填；仍无译文的行显示占位符。
  for (const span of container.querySelectorAll('[data-keyword-zh-for]')) {
    applyTranslationToSpan(span, { final: true });
  }
}
